import React, { useState, useEffect } from 'react';
import { appointmentAPI } from '../../services/api';
import { formatDate, formatTime } from '../../utils/dateTimeHelpers';
import '../../styles/AdminStyles.css';

function AdminAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(30);
  const [expandedDates, setExpandedDates] = useState({});
  const [expandedTimeSlots, setExpandedTimeSlots] = useState({});

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const data = await appointmentAPI.getAllAppointments();
      setAppointments(data);

      // Auto-expand future dates, collapse past dates
      const today = new Date().toDateString();
      const initialExpandedDates = {};
      data.forEach(apt => {
        const aptDate = new Date(apt.training_date);
        const isPast = aptDate < new Date(today);
        if (!isPast && initialExpandedDates[apt.training_date] === undefined) {
          initialExpandedDates[apt.training_date] = true;
        }
      });
      setExpandedDates(initialExpandedDates);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      alert('שגיאה בטעינת תורים');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (
      !window.confirm(
        `האם אתה בטוח שברצונך למחוק תורים מעל ${cleanupDays} ימים?`
      )
    ) {
      return;
    }

    try {
      const result = await appointmentAPI.cleanupOldAppointments(cleanupDays);
      alert(`${result.deleted_count} תורים נמחקו בהצלחה`);
      setShowCleanupModal(false);
      fetchAppointments();
    } catch (error) {
      console.error('Error cleaning up appointments:', error);
      alert('שגיאה בניקוי תורים');
    }
  };

  const handleCancelAppointment = async (appointmentId, userName) => {
    if (
      !window.confirm(
        `האם אתה בטוח שברצונך לבטל את התור של ${userName}?`
      )
    ) {
      return;
    }

    try {
      await appointmentAPI.adminCancelAppointment(appointmentId);
      alert('התור בוטל בהצלחה');
      fetchAppointments();
    } catch (error) {
      console.error('Error canceling appointment:', error);
      alert('שגיאה בביטול התור');
    }
  };

  const toggleDateExpanded = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const toggleTimeSlotExpanded = (dateTimeKey) => {
    setExpandedTimeSlots(prev => ({
      ...prev,
      [dateTimeKey]: !prev[dateTimeKey]
    }));
  };

  const filteredAppointments = appointments.filter((apt) => {
    const matchesSearch =
      apt.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apt.phone?.includes(searchTerm);

    const matchesDate = filterDate ? apt.training_date === filterDate : true;

    return matchesSearch && matchesDate;
  });

  const groupAppointmentsByDateAndTime = (appointments) => {
    const grouped = {};

    appointments.forEach((apt) => {
      if (!grouped[apt.training_date]) {
        grouped[apt.training_date] = {};
      }
      if (!grouped[apt.training_date][apt.training_time]) {
        grouped[apt.training_date][apt.training_time] = {
          appointments: [],
          maxParticipants: apt.max_participants || 5,
          trainingType: apt.training_type
        };
      }
      grouped[apt.training_date][apt.training_time].appointments.push(apt);
      // Update max_participants if available
      if (apt.max_participants) {
        grouped[apt.training_date][apt.training_time].maxParticipants = apt.max_participants;
      }
    });

    // Sort dates chronologically (closest first)
    return Object.keys(grouped)
      .sort()
      .map((date) => ({
        date,
        timeSlots: Object.keys(grouped[date])
          .sort()
          .map((time) => ({
            time,
            ...grouped[date][time]
          }))
      }));
  };

  const groupedAppointments = groupAppointmentsByDateAndTime(filteredAppointments);

  const getAppointmentStats = () => {
    const now = new Date();
    const upcoming = appointments.filter(
      (apt) => new Date(`${apt.training_date} ${apt.training_time}`) > now
    );
    const past = appointments.filter(
      (apt) => new Date(`${apt.training_date} ${apt.training_time}`) <= now
    );

    return {
      total: appointments.length,
      upcoming: upcoming.length,
      past: past.length,
    };
  };

  const stats = getAppointmentStats();

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>טוען תורים...</p>
      </div>
    );
  }

  return (
    <div className="admin-appointments">
      <div className="admin-header">
        <div>
          <h1>תורים</h1>
          <p>סך הכל {appointments.length} תורים</p>
        </div>
        <button
          className="admin-button-secondary"
          onClick={() => setShowCleanupModal(true)}
        >
          🗑️ נקה תורים ישנים
        </button>
      </div>

      {/* Stats */}
      <div className="admin-stats-row">
        <div className="admin-stat-item">
          <span className="admin-stat-label">סך הכל:</span>
          <span className="admin-stat-value">{stats.total}</span>
        </div>
        <div className="admin-stat-item">
          <span className="admin-stat-label">קרובים:</span>
          <span className="admin-stat-value">{stats.upcoming}</span>
        </div>
        <div className="admin-stat-item">
          <span className="admin-stat-label">עברו:</span>
          <span className="admin-stat-value">{stats.past}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-filters-bar">
        <input
          type="text"
          placeholder="🔍 חפש לפי שם או טלפון"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="admin-search-input"
        />
        <input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="admin-search-input"
        />
        {(searchTerm || filterDate) && (
          <button
            className="admin-button-secondary"
            onClick={() => {
              setSearchTerm('');
              setFilterDate('');
            }}
          >
            נקה
          </button>
        )}
      </div>

      {/* Appointments List */}
      <div className="admin-appointments-list">
        {groupedAppointments.map(({ date, timeSlots }) => {
          const dateIsPast = new Date(date) < new Date(new Date().toDateString());
          const isDateExpanded = expandedDates[date] || false;
          const totalAppointmentsForDate = timeSlots.reduce(
            (sum, slot) => sum + slot.appointments.length, 0
          );

          return (
            <div key={date} className={`admin-date-group ${dateIsPast ? 'admin-date-past' : ''}`}>
              <div
                className="admin-date-header admin-date-header-clickable"
                onClick={() => toggleDateExpanded(date)}
              >
                <div className="admin-date-header-content">
                  <span className={`admin-expand-icon ${isDateExpanded ? 'expanded' : ''}`}>
                    ▶
                  </span>
                  <span className="admin-date-title">{formatDate(date, true)}</span>
                  <span className="admin-date-count">({totalAppointmentsForDate} תורים)</span>
                  {dateIsPast && <span className="admin-date-past-badge">עבר</span>}
                </div>
              </div>

              {isDateExpanded && (
                <div className="admin-time-slots">
                  {timeSlots.map(({ time, appointments: timeAppointments, maxParticipants, trainingType }) => {
                    const dateTimeKey = `${date}-${time}`;
                    const isTimeSlotExpanded = expandedTimeSlots[dateTimeKey] || false;
                    const bookedCount = timeAppointments.length;
                    const isPast = new Date(`${date} ${time}`) < new Date();

                    return (
                      <div
                        key={time}
                        className={`admin-time-slot ${isPast ? 'admin-time-slot-past' : ''}`}
                      >
                        <div
                          className="admin-time-slot-header"
                          onClick={() => toggleTimeSlotExpanded(dateTimeKey)}
                        >
                          <div className="admin-time-slot-info">
                            <span className={`admin-expand-icon small ${isTimeSlotExpanded ? 'expanded' : ''}`}>
                              ▶
                            </span>
                            <span className="admin-time-slot-time">
                              {formatTime(time)}
                            </span>
                            <span className="admin-time-slot-count">
                              {bookedCount}/{maxParticipants}
                            </span>
                            {trainingType && (
                              <span className={`admin-badge admin-badge-${trainingType}`}>
                                {trainingType}
                              </span>
                            )}
                          </div>
                          <div className="admin-time-slot-bar">
                            <div
                              className="admin-time-slot-bar-fill"
                              style={{ width: `${(bookedCount / maxParticipants) * 100}%` }}
                            />
                          </div>
                        </div>

                        {isTimeSlotExpanded && (
                          <div className="admin-time-slot-appointments">
                            {timeAppointments.map((apt, index) => (
                              <div key={index} className="admin-appointment-item">
                                <div className="admin-appointment-item-info">
                                  <span className="admin-appointment-item-name">
                                    {apt.full_name || 'לא ידוע'}
                                  </span>
                                  <a
                                    href={`tel:${apt.phone}`}
                                    className="admin-appointment-item-phone"
                                  >
                                    {apt.phone}
                                  </a>
                                </div>
                                <div className="admin-appointment-item-actions">
                                  <a
                                    href={`https://wa.me/972${apt.phone?.replace(/^0/, '')}?text=שלום ${apt.full_name}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="admin-btn-whatsapp-small"
                                  >
                                    WhatsApp
                                  </a>
                                  <button
                                    className="admin-btn-cancel-small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelAppointment(apt.id, apt.full_name);
                                    }}
                                  >
                                    ביטול
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {groupedAppointments.length === 0 && (
          <div className="admin-empty-state">
            <p>לא נמצאו תורים</p>
          </div>
        )}
      </div>

      {/* Cleanup Modal */}
      {showCleanupModal && (
        <div
          className="admin-modal-overlay"
          onClick={() => setShowCleanupModal(false)}
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>נקה תורים ישנים</h2>
              <button
                className="admin-modal-close"
                onClick={() => setShowCleanupModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="admin-modal-content">
              <p>מחיקת תורים שעברו תעזור לשמור על המערכת מסודרת ומהירה.</p>

              <div className="admin-form-group">
                <label>מחק תורים מעל (ימים):</label>
                <input
                  type="number"
                  value={cleanupDays}
                  onChange={(e) => setCleanupDays(e.target.value)}
                  min="1"
                  max="365"
                />
              </div>

              <div className="admin-warning-box">
                ⚠️ פעולה זו תמחק לצמיתות תורים שעברו לפני {cleanupDays} ימים
              </div>
            </div>

            <div className="admin-modal-footer">
              <button
                className="admin-button-secondary"
                onClick={() => setShowCleanupModal(false)}
              >
                ביטול
              </button>
              <button className="admin-button-danger" onClick={handleCleanup}>
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAppointments;
